import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getEvolucaoIndicadores } from '../../services/api';
import { formatarMoeda } from '../../utils/formatters';
import { serializarFiltros } from '../../utils/compararFiltros';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const INDICADORES = [
  { value: 'litros', label: 'Litros vendidos' },
  { value: 'lucro', label: 'Lucro bruto' },
  { value: 'faturamento', label: 'Total vendido' },
];

const PERIODOS = [
  { value: 'dia', label: 'Diário' },
  { value: 'semana', label: 'Semanal' },
  { value: 'mes', label: 'Mensal' },
];

const ORDEM_DIAS_SEMANA = {
  'Segunda-feira': 1, 'Terça-feira': 2, 'Quarta-feira': 3, 'Quinta-feira': 4,
  'Sexta-feira': 5, 'Sábado': 6, 'Domingo': 7,
};

const GraficoEvolucao = ({ filtros = {}, onCrossFilter, filtroCruzadoAtivo }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [indicador, setIndicador] = useState('litros');
  const [periodo, setPeriodo] = useState('dia');
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartSize, setChartSize] = useState({ width: 800 });

  // Detectar tamanho do container para responsividade do gráfico
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setChartSize({ width: containerRef.current.offsetWidth });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Valores responsivos baseados na largura do container E número de barras
  const responsiveValues = useMemo(() => {
    const w = chartSize.width;
    const numBarras = dados.length || 1;

    // Calcular largura disponível por barra (com margem)
    const larguraPorBarra = w / numBarras;

    // Espessura da barra proporcional ao espaço disponível (60% do espaço)
    let barThickness = Math.max(4, Math.min(larguraPorBarra * 0.6, 32));

    // Ajustar tamanhos de fonte baseado na largura do container
    let fontSize, tickSize, labelSize, borderRadius;

    if (w < 300) {
      fontSize = 5; tickSize = 7; labelSize = 6; borderRadius = 3;
      barThickness = Math.min(barThickness, 8);
    } else if (w < 400) {
      fontSize = 6; tickSize = 8; labelSize = 7; borderRadius = 4;
      barThickness = Math.min(barThickness, 10);
    } else if (w < 500) {
      fontSize = 7; tickSize = 9; labelSize = 8; borderRadius = 5;
      barThickness = Math.min(barThickness, 14);
    } else if (w < 650) {
      fontSize = 9; tickSize = 10; labelSize = 9; borderRadius = 6;
      barThickness = Math.min(barThickness, 18);
    } else if (w < 800) {
      fontSize = 11; tickSize = 12; labelSize = 10; borderRadius = 8;
      barThickness = Math.min(barThickness, 22);
    } else if (w < 1000) {
      fontSize = 13; tickSize = 14; labelSize = 11; borderRadius = 10;
      barThickness = Math.min(barThickness, 26);
    } else {
      fontSize = 15; tickSize = 16; labelSize = 13; borderRadius = 12;
    }

    return { barThickness: Math.round(barThickness), fontSize, tickSize, labelSize, borderRadius };
  }, [chartSize.width, dados.length]);

  const dadosOrdenados = useMemo(() => {
    return [...dados].sort((a, b) => {
      const periodoA = a['visao_geral.indicador_selecionado_periodo'] || '';
      const periodoB = b['visao_geral.indicador_selecionado_periodo'] || '';
      if (ORDEM_DIAS_SEMANA[periodoA] && ORDEM_DIAS_SEMANA[periodoB]) {
        return ORDEM_DIAS_SEMANA[periodoA] - ORDEM_DIAS_SEMANA[periodoB];
      }
      if (!isNaN(periodoA) && !isNaN(periodoB)) return Number(periodoA) - Number(periodoB);
      if (periodoA.includes('-') && periodoB.includes('-')) return periodoA.localeCompare(periodoB);
      return 0;
    });
  }, [dados]);

  const extrairAnoMes = (filtroData) => {
    if (!filtroData || typeof filtroData !== 'string') return null;
    const data = filtroData.includes(' to ') ? filtroData.split(' to ')[0].trim() : filtroData;
    const partes = data.split('-');
    if (partes.length >= 2) return { ano: parseInt(partes[0]), mes: parseInt(partes[1]) };
    return null;
  };

  const handleChartClick = (event) => {
    if (!chartRef.current || !onCrossFilter) return;
    const elements = getElementAtEvent(chartRef.current, event);
    if (elements.length === 0) return;

    const itemClicado = dadosOrdenados[elements[0].index];
    if (!itemClicado) return;

    const periodoClicado = itemClicado['visao_geral.indicador_selecionado_periodo'] || '';
    if (periodo === 'semana') return;

    const hoje = new Date();
    let { ano, mes } = extrairAnoMes(filtros['visao_geral.data_hora_abastecimento_sp_date']) || {
      ano: hoje.getFullYear(),
      mes: hoje.getMonth() + 1,
    };

    if (!ano && dadosOrdenados.length > 0 && periodo === 'mes') {
      const primeiroPeriodo = dadosOrdenados[0]['visao_geral.indicador_selecionado_periodo'] || '';
      const info = extrairAnoMes(primeiroPeriodo);
      if (info) ({ ano, mes } = info);
    }

    let dataFiltro = null;
    if (periodo === 'dia') {
      dataFiltro = `${ano}-${String(mes).padStart(2, '0')}-${String(periodoClicado).padStart(2, '0')}`;
    } else if (periodo === 'mes') {
      dataFiltro = periodoClicado.includes('-') ? periodoClicado : `${ano}-${String(periodoClicado).padStart(2, '0')}`;
    }

    if (dataFiltro) onCrossFilter('data', dataFiltro, 'Gráfico de Evolução');
  };

  // Memoizar a serialização dos filtros para evitar chamadas desnecessárias
  const filtrosSerializados = useMemo(() => serializarFiltros(filtros), [filtros]);
  const fetchTimeoutRef = useRef(null);

  useEffect(() => {
    // Limpar timeout anterior se existir
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Delay maior para dar tempo do prefetch completar (batch de 39 requests leva ~10s)
    // Se o prefetch já completou, o cache estará quente e retornará instantaneamente
    fetchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resultado = await getEvolucaoIndicadores(filtros, indicador, periodo);
        setDados(resultado || []);
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico:', error);
        setDados([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms delay para dar tempo do prefetch completar

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [indicador, periodo, filtrosSerializados]);

  const { labels, valores } = useMemo(() => {
    const labels = dadosOrdenados.map(item => {
      const periodoItem = item['visao_geral.indicador_selecionado_periodo'] || '';
      return !isNaN(periodoItem) && periodoItem !== '' ? String(periodoItem).padStart(2, '0') : periodoItem;
    });
    const valores = dadosOrdenados.map(item => {
      const valor = item['visao_geral.indicador_selecionado'] || 0;
      return typeof valor === 'string' ? parseFloat(valor) : valor;
    });
    return { labels, valores };
  }, [dadosOrdenados]);

  const estatisticas = useMemo(() => {
    if (valores.length === 0) {
      return { total: 0, media: 0, maiorValor: 0, menorValor: 0, percentualVariacao: 0 };
    }
    const total = valores.reduce((acc, val) => acc + val, 0);
    const media = total / valores.length;
    const maiorValor = Math.max(...valores);
    const menorValor = Math.min(...valores);
    const percentualVariacao = valores.length >= 2 && valores[0] !== 0
      ? ((valores[valores.length - 1] - valores[0]) / valores[0]) * 100
      : 0;
    return { total, media, maiorValor, menorValor, percentualVariacao };
  }, [valores]);

  const formatarValor = useMemo(() => (valor, tipo = 'tooltip') => {
    const isLitros = indicador === 'litros';
    if (tipo === 'abreviado') {
      if (isLitros) {
        if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M L';
        if (valor >= 1000) return (valor / 1000).toFixed(1) + 'K L';
        return valor.toFixed(0) + ' L';
      }
      if (valor >= 1000000) return 'R$ ' + (valor / 1000000).toFixed(1) + 'M';
      if (valor >= 1000) return 'R$ ' + (valor / 1000).toFixed(1) + 'K';
      return formatarMoeda(valor);
    }
    if (tipo === 'barra') {
      return isLitros
        ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valor) + ' L'
        : formatarMoeda(valor);
    }
    return isLitros
      ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(valor) + ' L'
      : formatarMoeda(valor);
  }, [indicador]);

  const isBarraSelecionada = useMemo(() => {
    return (index) => {
      if (!filtroCruzadoAtivo?.valor) return false;
      const item = dadosOrdenados[index];
      if (!item) return false;
      const periodoItem = item['visao_geral.indicador_selecionado_periodo'] || '';
      const filtroData = filtroCruzadoAtivo.valor;
      if (periodo === 'dia') return filtroData.endsWith(`-${String(periodoItem).padStart(2, '0')}`);
      if (periodo === 'semana') return filtroData === periodoItem;
      if (periodo === 'mes') {
        if (periodoItem.includes('-')) return filtroData === periodoItem || filtroData.startsWith(periodoItem);
        const mesStr = String(periodoItem).padStart(2, '0');
        return filtroData.includes(`-${mesStr}-`) || filtroData.endsWith(`-${mesStr}`);
      }
      return false;
    };
  }, [filtroCruzadoAtivo, dadosOrdenados, periodo]);

  const cores = useMemo(() => {
    return valores.map((_, index) => (isBarraSelecionada(index) ? '#f59e0b' : '#5b7ba3'));
  }, [valores, isBarraSelecionada]);

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: indicador,
      data: valores,
      backgroundColor: cores,
      borderRadius: { topLeft: responsiveValues.borderRadius, topRight: responsiveValues.borderRadius, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: false,
      barThickness: responsiveValues.barThickness,
      maxBarThickness: responsiveValues.barThickness + 4,
    }],
  }), [labels, valores, cores, indicador, responsiveValues]);

  // Calcular máximo uma vez para usar nas funções de datalabels
  const maxValor = useMemo(() => {
    if (valores.length === 0) return 1;
    const max = Math.max(...valores);
    return max > 0 ? max : 1;
  }, [valores]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        anchor: (context) => {
          const dataIndex = context.dataIndex;
          const barValue = valores[dataIndex];
          if (!barValue || barValue <= 0 || maxValor <= 0) return 'end';
          const percentualAltura = (barValue / maxValor) * 100;
          return percentualAltura >= 35 ? 'center' : 'end';
        },
        align: (context) => {
          const dataIndex = context.dataIndex;
          const barValue = valores[dataIndex];
          if (!barValue || barValue <= 0 || maxValor <= 0) return 'top';
          const percentualAltura = (barValue / maxValor) * 100;
          return percentualAltura >= 35 ? 'center' : 'top';
        },
        clamp: false,
        clip: false,
        color: (context) => {
          const dataIndex = context.dataIndex;
          const barValue = valores[dataIndex];
          if (!barValue || barValue <= 0 || maxValor <= 0) return '#374151';
          const percentualAltura = (barValue / maxValor) * 100;
          return percentualAltura >= 35 ? '#ffffff' : '#374151';
        },
        font: { size: responsiveValues.fontSize, weight: 'bold' },
        rotation: -90,
        offset: (context) => {
          const dataIndex = context.dataIndex;
          const barValue = valores[dataIndex];
          if (!barValue || barValue <= 0 || maxValor <= 0) return 12;
          const percentualAltura = (barValue / maxValor) * 100;
          return percentualAltura >= 35 ? 0 : 12;
        },
        padding: 0,
        formatter: (value) => formatarValor(value, 'barra'),
        textStrokeColor: (context) => {
          const dataIndex = context.dataIndex;
          const barValue = valores[dataIndex];
          if (!barValue || barValue <= 0 || maxValor <= 0) return 'transparent';
          const percentualAltura = (barValue / maxValor) * 100;
          return percentualAltura >= 35 ? 'rgba(0, 0, 0, 0.4)' : 'transparent';
        },
        textStrokeWidth: 1,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#f59e0b',
        borderWidth: 2,
        padding: Math.max(6, responsiveValues.fontSize * 0.75),
        cornerRadius: 8,
        displayColors: false,
        titleFont: { size: responsiveValues.labelSize, weight: 'normal' },
        bodyFont: { size: responsiveValues.fontSize, weight: 'bold' },
        callbacks: {
          title: () => '',
          label: (context) => formatarValor(context.raw),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', font: { size: responsiveValues.tickSize }, maxRotation: 0, minRotation: 0 },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { display: false },
        padding: { top: 10, bottom: 0 },
      },
    },
    interaction: { intersect: true, mode: 'index' },
    layout: {
      padding: { top: 1, right: 1, bottom: 1, left: 1 },
    },
  }), [formatarValor, responsiveValues, maxValor, valores]);

  return (
    <div ref={containerRef} className="dashboard-card overflow-hidden w-full max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between flex-shrink-0 w-full" style={{ gap: 'var(--space-fluid-sm)', marginBottom: 'var(--space-fluid-xs)' }}>
        <div className="flex border-b border-gray-200" style={{ gap: 0 }}>
          {INDICADORES.map((item) => (
            <button
              key={item.value}
              onClick={() => setIndicador(item.value)}
              className={`fluid-tab ${indicador === item.value ? 'text-orange-500' : 'text-gray-600 hover:text-gray-800'}`}
            >
              {item.label}
              {indicador === item.value && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500"></span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="fluid-select">
            {PERIODOS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <svg className="absolute top-1/2 -translate-y-1/2 text-orange-500 pointer-events-none" style={{ right: 'var(--space-fluid-xs)', width: 'var(--icon-fluid-sm)', height: 'var(--icon-fluid-sm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      <div className="flex-1 min-h-0 cursor-pointer w-full max-w-full overflow-hidden" style={{ minHeight: '150px' }}>
        {loading ? (
          <div className="fluid-spinner-container">
            <div className="flex flex-col items-center" style={{ gap: 'var(--space-fluid-sm)' }}>
              <div className="fluid-spinner"></div>
              <span className="fluid-spinner-text">Carregando dados...</span>
            </div>
          </div>
        ) : dados.length > 0 ? (
          <div className="w-full h-full">
            <Bar ref={chartRef} data={chartData} options={chartOptions} onClick={handleChartClick} />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500" style={{ fontSize: 'var(--text-fluid-sm)' }}>Nenhum dado disponível</div>
        )}
      </div>
      {!loading && dados.length > 0 && (
        <div className="flex flex-wrap items-center border-t border-gray-300 flex-shrink-0" style={{ gap: 'var(--space-fluid-xs)', paddingTop: 'var(--space-fluid-xs)' }}>
          <div className="fluid-stat-badge">
            <span className="fluid-legend-dot bg-[#5b7ba3]"></span>
            <span className="fluid-stat-label">{INDICADORES.find(item => item.value === indicador)?.label}</span>
          </div>
          <div className="flex-1 flex flex-wrap justify-end" style={{ gap: 'var(--space-fluid-xs)' }}>
            <div className="fluid-stat-badge">
              <span className="fluid-stat-label">Total</span>
              <span className="fluid-stat-value">{formatarValor(estatisticas.total, 'abreviado')}</span>
            </div>
            <div className="fluid-stat-badge">
              <span className="fluid-stat-label">Média</span>
              <span className="fluid-stat-value">{formatarValor(estatisticas.media, 'abreviado')}</span>
              {estatisticas.percentualVariacao !== 0 && (
                <span className={`font-medium ${estatisticas.percentualVariacao >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontSize: 'var(--text-fluid-xs)' }}>
                  ({estatisticas.percentualVariacao >= 0 ? '+' : ''}{estatisticas.percentualVariacao.toFixed(1)}%)
                </span>
              )}
            </div>
            <div className="fluid-stat-badge">
              <span className="text-green-600 font-bold" style={{ fontSize: 'var(--text-fluid-xs)' }}>▲</span>
              <span className="fluid-stat-label">Maior</span>
              <span className="fluid-stat-value">{formatarValor(estatisticas.maiorValor, 'abreviado')}</span>
            </div>
            <div className="fluid-stat-badge">
              <span className="text-red-600 font-bold" style={{ fontSize: 'var(--text-fluid-xs)' }}>▼</span>
              <span className="fluid-stat-label">Menor</span>
              <span className="fluid-stat-value">{formatarValor(estatisticas.menorValor, 'abreviado')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraficoEvolucao;
